/**
 * 運営管理画面用サービス（/admin/）
 * システム管理者（operator ロール）が使用
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';

import type { Client, Race, TimingLocation } from '../types';
import { normalizeKana } from '../utils/kana';
import { db } from './firebase';

// ========================================
// 主催者管理
// ========================================

/** 主催者一覧取得 */
export async function getClients(): Promise<Client[]> {
  const clientsRef = collection(db, 'clients');
  const q = query(clientsRef, where('deletedAt', '==', null), orderBy('createdAt', 'desc'));

  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (docData) =>
      ({
        id: docData.id,
        ...docData.data(),
      }) as Client
  );
}

/** 主催者取得 */
export async function getClient(clientId: string): Promise<Client | null> {
  const clientRef = doc(db, 'clients', clientId);
  const clientDoc = await getDoc(clientRef);

  if (!clientDoc.exists()) {
    return null;
  }

  return {
    id: clientDoc.id,
    ...clientDoc.data(),
  } as Client;
}

/** 主催者作成用の入力データ */
export interface CreateClientInput {
  name: string;
  description: string;
}

/** 主催者作成 */
export async function createClient(input: CreateClientInput): Promise<string> {
  const clientRef = doc(collection(db, 'clients'));

  await setDoc(clientRef, {
    name: input.name,
    description: input.description,
    links: [],
    twitter: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    deletedAt: null,
  });

  return clientRef.id;
}

/** 主催者更新用の入力データ */
export interface UpdateClientInput {
  name: string;
  description: string;
}

/** 主催者更新 */
export async function updateClient(clientId: string, input: UpdateClientInput): Promise<void> {
  const clientRef = doc(db, 'clients', clientId);

  await updateDoc(clientRef, {
    name: input.name,
    description: input.description,
    updatedAt: serverTimestamp(),
  });
}

// ========================================
// 大会管理
// ========================================

/** 大会一覧取得 */
export async function getRaces(): Promise<Race[]> {
  const racesRef = collection(db, 'races');
  const q = query(racesRef, where('deletedAt', '==', null), orderBy('createdAt', 'desc'));

  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (docData) =>
      ({
        id: docData.id,
        ...docData.data(),
      }) as Race
  );
}

/** 主催者に紐づく大会一覧取得 */
export async function getRacesByClientId(clientId: string): Promise<Race[]> {
  const racesRef = collection(db, 'races');
  const q = query(
    racesRef,
    where('clientId', '==', clientId),
    where('deletedAt', '==', null),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(
    (docData) =>
      ({
        id: docData.id,
        ...docData.data(),
      }) as Race
  );
}

/** 大会取得 */
export async function getRace(raceId: string): Promise<Race | null> {
  const raceRef = doc(db, 'races', raceId);
  const raceDoc = await getDoc(raceRef);

  if (!raceDoc.exists()) {
    return null;
  }

  return {
    id: raceDoc.id,
    ...raceDoc.data(),
  } as Race;
}

/** 大会作成用の入力データ */
export interface CreateRaceInput {
  clientId: string;
  raceName: string;
  alias: string;
  startAt: Date;
  endAt: Date;
}

/** 大会作成 */
export async function createRace(input: CreateRaceInput): Promise<string> {
  const raceRef = doc(collection(db, 'races'));

  await setDoc(raceRef, {
    clientId: input.clientId,
    raceName: input.raceName,
    alias: input.alias,
    raceDate: null,
    startAt: input.startAt,
    endAt: input.endAt,
    // コントラクト関連は null で初期化
    chain: null,
    contractAddress: null,
    contractName: null,
    contractSymbol: null,
    contractDescription: null,
    deployStatus: null,
    deployedAt: null,
    ownerAddress: null,
    // NFT 関連も null
    tokenName: null,
    tokenDescription: null,
    // 計測連携関連
    timingEventId: null,
    locations: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    deletedAt: null,
  });

  return raceRef.id;
}

/** 大会更新用の入力データ */
export interface UpdateRaceInput {
  raceName: string;
  clientId: string;
  raceDate: string;
  startAt: Date;
  endAt: Date;
}

/** 大会更新 */
export async function updateRace(raceId: string, input: UpdateRaceInput): Promise<void> {
  const raceRef = doc(db, 'races', raceId);

  await updateDoc(raceRef, {
    raceName: input.raceName,
    clientId: input.clientId,
    raceDate: input.raceDate,
    startAt: input.startAt,
    endAt: input.endAt,
    updatedAt: serverTimestamp(),
  });
}

/** NFT情報更新用の入力データ */
export interface UpdateRaceNftInfoInput {
  tokenName: string | null;
  tokenDescription: string | null;
}

/** 大会のNFT情報を更新 */
export async function updateRaceNftInfo(raceId: string, input: UpdateRaceNftInfoInput): Promise<void> {
  const raceRef = doc(db, 'races', raceId);

  await updateDoc(raceRef, {
    tokenName: input.tokenName,
    tokenDescription: input.tokenDescription,
    updatedAt: serverTimestamp(),
  });
}

/** 計測システム連携情報更新用の入力データ */
export interface UpdateRaceTimingInfoInput {
  timingEventId: string | null;
  locations: TimingLocation[];
}

/** 大会の計測システム連携情報を更新 */
export async function updateRaceTimingInfo(raceId: string, input: UpdateRaceTimingInfoInput): Promise<void> {
  const raceRef = doc(db, 'races', raceId);

  await updateDoc(raceRef, {
    timingEventId: input.timingEventId,
    locations: input.locations,
    updatedAt: serverTimestamp(),
  });
}

// ========================================
// ランナーインポート
// ========================================

/** ランナー情報インポート用の入力データ */
export interface RaceRunnerInput {
  bib: string;
  fullName: string;
  fullNameKana: string;
  birth: string;
  email: string;
  raceId: string;
  raceAlias: string;
}

/** ランナー情報をバッチインポート（200件ずつ） */
export async function importRaceRunners(runners: RaceRunnerInput[]): Promise<number> {
  const BATCH_SIZE = 200;
  let importedCount = 0;

  for (let i = 0; i < runners.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    const chunk = runners.slice(i, i + BATCH_SIZE);

    for (const runner of chunk) {
      const { katakana, hiragana } = normalizeKana(runner.fullNameKana);
      // docId: ${raceAlias}-${bib} 形式
      const runnerId = `${runner.raceAlias}-${runner.bib}`;
      const runnerRef = doc(db, 'runners', runnerId);
      // 8文字のランダム文字列（QRコード用）
      const timingHash = Math.random().toString(36).slice(-8);
      batch.set(
        runnerRef,
        {
          bib: runner.bib,
          fullName: runner.fullName,
          fullName_kana: runner.fullNameKana,
          fullName_katakana: katakana,
          fullName_hiragana: hiragana,
          birth: runner.birth,
          email: runner.email,
          raceId: runner.raceId,
          raceAlias: runner.raceAlias,
          // timing フィールドは計測データ受信時に設定される
          timing: null,
          timingHash,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    }

    await batch.commit();
    importedCount += chunk.length;
  }

  return importedCount;
}
